import AttackDemonstrationsTab from '@/components/AttackDemonstrationsTab';
import { roleGroups } from '@/lib/roles';
import {
  AlertTriangle,
  Bell,
  Bot,
  Download,
  FileText,
  LayoutDashboard,
  Package,
  Settings,
  Shield,
  ShieldCheck,
  ShieldPlus,
  ShieldUser,
  Users,
  type LucideIcon,
} from 'lucide-react';
import AuditLogsTab from './tabs/DataTableAuditLogsTab';
import SecureAiCodingTab from './tabs/SecureAiCodingTab';
import ThreatModelingTab from './tabs/ThreatModelingTab';
import { LlmVulnDiscoveryPage } from '@/components/LlmVulnDiscovery/LlmVulnDiscoveryPage';
import SettingsTab from './tabs/SettingsTab';
import MembersTab from './tabs/MembersTab';
import SecurityGroupsTab from './tabs/SecurityGroupsTab';
import OrgShadowAITab from './tabs/OrgShadowAITab';
import { PostureManagement } from '@/components/Inventory';
import PoliciesTab from './tabs/PoliciesTab';
import InstallTab from '@/components/InstallTab/InstallTab';
import type { RolesConfig } from '@/hooks/useRoleCheck';
import type { ComponentType } from 'react';
import { Dashboard } from './tabs/Dashboard';
import OrgGroupAlertsTab from './tabs/OrgGroupAlertsTab';
import AgentsActivityTab from './tabs/AgentsActivityTab';

import { getSubheaderForTab, OrgTab, type OrgTabType } from './orgTabKeys';
export { getSubheaderForTab, OrgTab, type OrgTabType };

/**
 * Tab configuration: labels, icons, components, and role requirements
 *
 * KIR-4480: key insertion order IS the sidebar order — `OrganizationView`
 * renders `Object.keys(tabConfig)`. `Install` ("Get Started") leads only until
 * the org has an API key; `useReorderTabsOnSetup` then moves it after
 * `AttackDemonstrations`, which is the order the ticket specifies.
 */
export type TabComponentType = ComponentType<unknown>;
export type OrgTabConfig = Record<
  OrgTabType,
  {
    label: string;
    icon: LucideIcon;
    component: TabComponentType;
    roles?: RolesConfig;
  }
>;
export const TAB_CONFIG: OrgTabConfig = {
  [OrgTab.Install]: {
    label: 'Get Started',
    icon: Download,
    component: InstallTab as TabComponentType,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
  [OrgTab.Dashboard]: {
    label: 'Dashboard',
    icon: LayoutDashboard,
    component: Dashboard as TabComponentType,
    roles: {
      param: 'organizationId',
      anyOf: [...roleGroups.managerOwnerOrSupport],
    },
  },
  [OrgTab.PostureManagement]: {
    label: 'Inventory',
    icon: ShieldCheck,
    component: PostureManagement as TabComponentType,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
  [OrgTab.Alerts]: {
    label: 'Alerts',
    icon: Bell,
    component: OrgGroupAlertsTab,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
  [OrgTab.ShadowAI]: {
    label: 'Shadow AI',
    icon: Bot,
    component: OrgShadowAITab,
    roles: {
      param: 'organizationId',
      anyOf: [...roleGroups.managerOwnerOrSupport],
    },
  },
  [OrgTab.AgentsActivity]: {
    label: 'Packages & Repos',
    icon: Package,
    component: AgentsActivityTab as TabComponentType,
    roles: {
      param: 'organizationId',
      anyOf: [...roleGroups.managerOwnerOrSupport],
    },
  },
  [OrgTab.Policies]: {
    label: 'Policies',
    icon: Shield,
    component: PoliciesTab,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
  [OrgTab.SecureAiCoding]: {
    label: 'Secure AI Coding Enforcement',
    icon: ShieldPlus,
    component: SecureAiCodingTab as TabComponentType,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
  [OrgTab.ThreatModeling]: {
    label: 'Threat Modeling',
    icon: ShieldPlus,
    component: ThreatModelingTab as TabComponentType,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
  [OrgTab.LlmVulnDiscovery]: {
    label: 'Vulnerability Discovery with LLM',
    icon: ShieldPlus,
    component: LlmVulnDiscoveryPage as TabComponentType,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
  [OrgTab.Users]: {
    label: 'Users',
    icon: Users,
    component: MembersTab,
    roles: {
      param: 'organizationId',
      anyOf: [...roleGroups.managerOwnerOrSupport],
    },
  },
  [OrgTab.SecurityGroups]: {
    label: 'Security Groups',
    icon: ShieldUser,
    component: SecurityGroupsTab as TabComponentType,
    // KIR-5086: support reads everything (KIR-4128) — the page renders for it
    // with every write control disabled. Writes stay owner/manager server-side.
    roles: {
      param: 'organizationId',
      anyOf: [...roleGroups.managerOwnerOrSupport],
    },
  },
  [OrgTab.Settings]: {
    label: 'Settings',
    icon: Settings,
    component: SettingsTab,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
  [OrgTab.AuditLogs]: {
    label: 'Audit Logs',
    icon: FileText,
    component: AuditLogsTab,
    roles: {
      param: 'organizationId',
      anyOf: [...roleGroups.managerOwnerOrSupport],
    },
  },
  [OrgTab.AttackDemonstrations]: {
    label: 'Attack Demonstrations',
    icon: AlertTriangle,
    component: AttackDemonstrationsTab,
    roles: { param: 'organizationId', anyOf: [...roleGroups.anyRole] },
  },
};
